import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button, Dropdown, DropdownButton, Modal, Table, Offcanvas } from 'react-bootstrap';
import { formatCurrency as formatCurrencyUtil } from '../utils/currencyConverter';

import {
  CircularProgressbar,
  buildStyles
} from 'react-circular-progressbar';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  CartesianGrid,
  Legend, RadialBarChart, RadialBar, AreaChart,
  Area,
} from 'recharts';
import { FiTrendingUp, FiTrendingDown, FiDollarSign, FiCreditCard, FiPieChart, FiCalendar, FiFileText, FiUsers, FiShoppingCart, FiClock, FiTarget, FiLayers } from 'react-icons/fi';
import "react-datepicker/dist/react-datepicker.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";

function FirstPage() {

  const [showIncomeExpenseModal, setShowIncomeExpenseModal] = useState(false);
  const [modalType, setModalType] = useState('income'); // 'income' or 'expense'

  const handleShowModal = (type) => {
    setModalType(type);
    setShowIncomeExpenseModal(true);
  };

  const [selectedFilter, setSelectedFilter] = useState("financialYear"); // Default to financialYear
  const [isDateFilterVisible, setIsDateFilterVisible] = useState(false);

  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [corporateTaxData, setCorporateTaxData] = useState(null);

  const [summary, setSummary] = useState({
    total_revenue: 0,
    net_profit_trend: [],
    top_products: [],
    receivables_trend: [],
    cash_flow: { in: 0, out: 0 },
    vat: { in: 0, out: 0 },
    summary_by_region: [],
    alerts: [],
    expense_summary: [],
    summary_dashboard: {
      total_summary: 0,
      summary_target: 0,
      conversion_rate: 0,
      top_summarypeople: [],
      summary_by_product: [],
      sales_by_region: [],
      avg_order_value: 0,
      new_customers: 0,
      pending_quotations: 0,
      pending_invoices: 0,
    },
  });
  useEffect(() => {
    console.log("FULL corporateTaxData:", corporateTaxData);
    console.log("Receivables object:", corporateTaxData?.receivables);
  }, [corporateTaxData]);
  useEffect(() => {
    console.log('SUMMARY RESPONSE:', summary);
  }, [summary]);

  const prepareReceivablesData = () => {
    const receivables = summary?.receivables || {};

    const buckets = [
      'Current',
      '1-30 Days',
      '31-60 Days',
      '61-90 Days',
      'Over 90 Days',
    ];

    return buckets.map(bucket => {
      const invoices = receivables[bucket] || [];

      const totalAmount = invoices.reduce(
        (sum, inv) => sum + Number(inv.amount || 0),
        0
      );

      return {
        name: bucket,
        value: totalAmount,
        count: invoices.length,
      };
    });
  };

  console.log("Receivables Data:", prepareReceivablesData());

  const preparePayablesData = () => {
    if (!summary.payables) return [];

    return Object.entries(summary.payables).map(([date, transactions]) => ({
      date,
      amount: transactions.reduce((sum, txn) => {
        const amt = parseFloat(txn.amount);
        return sum + (isNaN(amt) ? 0 : amt);
      }, 0)
    })).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const prepareCashFlowData = () => {
    const cashFlow = summary?.cash_flow;

    if (!Array.isArray(cashFlow)) return [];

    return cashFlow.map(item => ({
      day: item.day,
      month: item.month,
      net_cash: parseFloat(item.net_cash),
    }));
  };

  const prepareExpenseBreakdownData = () => {
    if (!summary.expense_breakdown) return [];
    return summary.expense_breakdown.map(item => ({
      name: item.category?.name || 'Uncategorized',
      value: item.total
    }));
  };

  const [fetchedYears, setFetchedYears] = useState({});

  const fetchCorporateTax = async (fromDate, toDate) => {
    console.log("🔍 fetchCorporateTax called with:", { fromDate, toDate });

    if (!fromDate || !toDate) {
      console.log("❌ Missing dates, skipping API call");
      return;
    }

    const key = `${fromDate}_${toDate}`;
    if (fetchedYears[key]) {
      console.log("📦 Using cached data for:", key);
      setCorporateTaxData(fetchedYears[key]);
      return;
    }

    try {
      console.log("🌐 Making API call to corporate tax endpoint");

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = JSON.parse(localStorage.getItem('user-info'))?.token;
      const origin = localStorage.getItem('user-origin');

      const response = await axios.get(`${backendUrl}/api/corporate-tax`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Tenant-Origin': origin
        },
        params: { startdate: fromDate, enddate: toDate }
      });

      console.log("✅ Corporate Tax API Response:", response.data);

      setCorporateTaxData(response.data);
      setFetchedYears(prev => ({ ...prev, [key]: response.data }));
    } catch (error) {
      console.error("❌ Error fetching corporate tax data:", error);
      console.error("Error details:", error.response?.data);
    }
  };

  const getFinancialYear = () => {
    const monthMap = {
      January: '01', February: '02', March: '03', April: '04',
      May: '05', June: '06', July: '07', August: '08',
      September: '09', October: '10', November: '11', December: '12'
    };

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonthNum = today.getMonth() + 1;

    try {
      const storedFormData = JSON.parse(localStorage.getItem('formData'));

      // 1. Try to get explicit months from settings
      let sMonthName = storedFormData?.start_month;
      let eMonthName = storedFormData?.end_month;

      // 2. Fallback to parsing the financial_year string if months aren't set
      if (!sMonthName || !eMonthName) {
        const fy = storedFormData?.financial_year;
        if (fy && fy.includes('-')) {
          const parts = fy.split('-').map(m => m.trim());
          if (isNaN(parseInt(parts[0]))) {
            sMonthName = parts[0];
            eMonthName = parts[1];
          } else {
            // Case: "2024-2025"
            return {
              startDate: `${parts[0]}-04-01`,
              endDate: `${parts[1]}-03-31`
            };
          }
        }
      }

      if (sMonthName && eMonthName) {
        const sMonth = monthMap[sMonthName];
        const eMonth = monthMap[eMonthName];

        if (sMonth && eMonth) {
          const sMonthInt = parseInt(sMonth);
          // If current month is before start month, started last year
          const startYear = currentMonthNum < sMonthInt ? currentYear - 1 : currentYear;
          const endYear = parseInt(eMonth) < sMonthInt ? startYear + 1 : startYear;
          const endDay = new Date(endYear, parseInt(eMonth), 0).getDate();

          return {
            startDate: `${startYear}-${sMonth}-01`,
            endDate: `${endYear}-${eMonth}-${endDay}`
          };
        }
      }
    } catch (e) {
      console.error("Failed to parse financial year, using fallback:", e);
    }

    // Default Fallback
    return {
      startDate: `${currentYear}-04-01`,
      endDate: `${currentYear + 1}-03-31`
    };
  };

  let cashBalance = summary?.account_summary?.cash || 0;
  let bankBalance = summary?.account_summary?.bank || 0;

  const totalBalance = summary?.account_summary?.total_balance || (cashBalance + bankBalance);

  const handleFinancialYearChange = (selectedYear) => {
    if (selectedYear === "financialYear") {
      setSelectedFilter("financialYear");
      setIsDateFilterVisible(false);

      const { startDate, endDate } = getFinancialYear();
      console.log("Financial Year Dates:", startDate, endDate);

      fetchSummary(startDate, endDate);
      // fetchData(startDate, endDate);

      // Pass individual dates, not the object
      if (startDate && endDate) {
        fetchCorporateTax(startDate, endDate);
      }
    } else {
      setSelectedFilter("customRange");
      setIsDateFilterVisible(true);
      setCustomDateRange({ startDate: null, endDate: null });
    }
  };

  // Handle custom date range selection
  const handleCustomDateChange = (startDate, endDate) => {
    setCustomDateRange({ startDate, endDate });
  };

  const handleClearDates = () => {
    setCustomDateRange({ startDate: null, endDate: null });
    setIsDateFilterVisible(false); // Hide date fields after clear
    fetchSummary(null, null);
    fetchCorporateTax(null, null);
  };

  const handleSubmit = () => {
    const { startDate, endDate } = customDateRange;
    console.log("🔄 Handle Submit:", { startDate, endDate });

    if (startDate && endDate) {
      // Ensure dates are in correct format (YYYY-MM-DD)
      const formattedStartDate = `${startDate}-01`; // Add day component
      const formattedEndDate = `${endDate}-31`; // Add day component

      console.log("📅 Formatted Dates:", { formattedStartDate, formattedEndDate });

      fetchSummary(formattedStartDate, formattedEndDate);
      fetchCorporateTax(formattedStartDate, formattedEndDate);
      setIsDateFilterVisible(false);
    } else {
      console.log("❌ Missing dates in handleSubmit");
    }
  };

  const fetchSummary = async (startDate, endDate) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const userInfo = JSON.parse(localStorage.getItem('user-info'));
      const origin = localStorage.getItem('user-origin');

      const token = userInfo?.token;

      const response = await axios.get(`${backendUrl}/api/dashboard`, {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          'Tenant-Origin': origin,
        },
      });

      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching data:", error.response?.data || error.message);
    }
  };
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const userInfo = JSON.parse(localStorage.getItem("user-info"));
  const token = userInfo?.token;
  const origin = localStorage.getItem("user-origin");

  // Exchange rate constant
  const EXCHANGE_RATE = 3.692;
  const convertToAED = (amount, currency) => {
    const numericAmount = parseFloat(amount) || 0;
    if (currency === 'USD') {
      return numericAmount * EXCHANGE_RATE;
    }
    return numericAmount; // Assuming AED is default
  };
  const [expenses, setExpenses] = useState([]);

  const fetchData = async (startDate, endDate) => {
    try {
      const response = await axios.get(`${backendUrl}/api/expense-report`, {
        params: {
          dateRange: 'custom',
          fromDate: startDate,
          toDate: endDate,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Tenant-Origin": origin,
        }
      });

      const data = response.data.data || [];
      const dataWithAED = data.map(expense => ({
        ...expense,
        amount_aed: convertToAED(expense.amount_excl_vat, expense.vendor_currency),
        vat_amount_aed: convertToAED(expense.vat_amount, expense.vendor_currency),
        adjustment_amount_aed: convertToAED(expense.adjustment_amount || 0, expense.vendor_currency),
        total_aed: convertToAED(expense.total, expense.vendor_currency)
      }));

      setExpenses(dataWithAED);
      calculateSummary(dataWithAED);
    } catch (error) {
      console.error("Error loading expense report:", error);
    }
  };
  const calculateSummary = (data) => {
    let totalAmount = 0;
    let totalVAT = 0;
    let adjustmentAmount = 0;
    let totalAmountAED = 0;
    let totalVATAED = 0;
    let adjustmentAmountAED = 0;
    let paymentMethods = {
      cash: 0,
      bank: 0,
      other: 0
    };
    let paymentMethodsAED = {
      cash: 0,
      bank: 0,
      other: 0
    };

    data.forEach(expense => {
      const amount = parseFloat(expense.amount_excl_vat);
      const vat = parseFloat(expense.vat_amount);
      const adjustment = parseFloat(expense.adjustment_amount || 0);
      const total = parseFloat(expense.total);

      const amountAED = parseFloat(expense.amount_aed);
      const vatAED = parseFloat(expense.vat_amount_aed);
      const adjustmentAED = parseFloat(expense.adjustment_amount_aed);
      const totalAED = parseFloat(expense.total_aed);

      totalAmount += amount;
      totalVAT += vat;
      adjustmentAmount += adjustment;

      totalAmountAED += amountAED;
      totalVATAED += vatAED;
      adjustmentAmountAED += adjustmentAED;

      if (expense.payment_mode === 'Cash') {
        paymentMethods.cash += total;
        paymentMethodsAED.cash += totalAED;
      } else if (expense.payment_mode === 'Bank') {
        paymentMethods.bank += total;
        paymentMethodsAED.bank += totalAED;
      } else {
        paymentMethods.other += total;
        paymentMethodsAED.other += totalAED;
      }
    });

    setSummary({
      totalAmount,
      totalVAT,
      adjustmentAmount,
      grandTotal: totalAmount + totalVAT + adjustmentAmount,
      totalAmountAED,
      totalVATAED,
      adjustmentAmountAED,
      grandTotalAED: totalAmountAED + totalVATAED + adjustmentAmountAED,
      paymentMethods,
      paymentMethodsAED
    });
  };

  useEffect(() => {
    const { startDate, endDate } = getFinancialYear();
    fetchSummary(startDate, endDate);

    // Fetch corporate tax data for financial year
    if (startDate && endDate) {
      fetchCorporateTax(startDate, endDate);
    } else {
      console.warn("⚠️ No valid financial year found, falling back to current calendar year");
      const year = new Date().getFullYear();
      fetchCorporateTax(`${year}-01-01`, `${year}-12-31`);
    }
  }, []);

  const barColors = ['#4F46E5', '#6366F1', '#818CF8', '#A5B4FC', '#C7D2FE'];
  const COLORS = ['#4F46E5', '#6366F1', '#818CF8', '#A5B4FC', '#C7D2FE'];
  const summarys = summary.sales_dashboard;
  const progressPercent = ((summary?.total_summary / summary?.summary_target) * 100).toFixed(1);
  const formatCurrency = (amount) => {
    return formatCurrencyUtil(amount);
  };
  const totalOrders = 100;
  const ordersInProgress = summary?.orders_in_progress ?? 0;
  const progressPercent1 = totalOrders ? (ordersInProgress / totalOrders) * 100 : 0;
  const totalTasks =
    (summary?.tasks_summary?.in_progress ?? 0) +
    (summary?.tasks_summary?.completed ?? 0) +
    (summary?.tasks_summary?.pending ?? 0);

  const completedPercent = totalTasks ? ((summary?.tasks_summary?.completed / totalTasks) * 100).toFixed(0) : 0;
  const inProgressPercent = totalTasks ? ((summary?.tasks_summary?.in_progress / totalTasks) * 100).toFixed(0) : 0;
  const pendingPercent = totalTasks ? ((summary?.tasks_summary?.pending / totalTasks) * 100).toFixed(0) : 0;

  const getCurrentQuarter = () => {
    const month = new Date().getMonth() + 1; // Jan = 0, so +1
    if (month >= 1 && month <= 3) return 'Q1';
    if (month >= 4 && month <= 6) return 'Q2';
    if (month >= 7 && month <= 9) return 'Q3';
    return 'Q4';
  };
  const currentQuarter = getCurrentQuarter();

  const currentVat = useMemo(() => {
    return summary?.tax_summary?.find(q => q.quarter === currentQuarter) || {};
  }, [summary]);

  const formatUSCurrency = (amount) => {
    return formatCurrencyUtil(amount);
  };

  // ---- Derived values for the KPI strip ----
  const totalRevenue = summary.revenue?.ytd ?? corporateTaxData?.total_income ?? 0;
  const totalExpense = summary.expense?.ytd ?? corporateTaxData?.total_expense ?? 0;
  const netProfit = summary.net_profit?.ytd ?? corporateTaxData?.net_profit ?? 0;
  const backendVatPayable = (summary?.vat?.out || 0) - (summary?.vat?.in || 0);
  const vatPayable = summary?.vat ? backendVatPayable : (corporateTaxData?.vat_payable || currentVat?.net_vat_liability || 0);
  const receivablesTotal = prepareReceivablesData().reduce((sum, b) => sum + b.value, 0);

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="erp-dashboard">
          <style>{`
            .erp-dashboard {
              --erp-primary: #4F46E5;
              --erp-primary-dark: #3730A3;
              --erp-primary-tint: #EEF2FF;
              --erp-success: #10B981;
              --erp-success-tint: #ECFDF5;
              --erp-danger: #EF4444;
              --erp-danger-tint: #FEF2F2;
              --erp-warning: #F59E0B;
              --erp-warning-tint: #FFFBEB;
              --erp-bg: #F8FAFC;
              --erp-surface: #FFFFFF;
              --erp-border: #E6E9F0;
              --erp-text: #0F172A;
              --erp-text-muted: #64748B;
              --erp-radius: 16px;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Roboto, sans-serif;
              color: var(--erp-text);
              background: var(--erp-bg);
              box-sizing: border-box;
              padding: 24px 28px 40px 32px;
              margin-left: 12px;
            }
            .erp-dashboard * { box-sizing: border-box; }

            .erp-header {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              flex-wrap: wrap;
              gap: 12px;
              margin-bottom: 20px;
            }
            .erp-header h4 {
              font-size: 22px;
              font-weight: 700;
              margin: 0;
              letter-spacing: -0.01em;
            }
            .erp-header p {
              margin: 4px 0 0;
              color: var(--erp-text-muted);
              font-size: 13px;
            }
            .erp-filter-bar {
              display: flex;
              align-items: center;
              gap: 10px;
              flex-wrap: wrap;
            }
            .erp-filter-bar .btn {
              border-radius: 8px !important;
            }

            /* KPI strip */
            .kpi-row {
              display: grid;
              grid-template-columns: repeat(4, minmax(0, 1fr));
              gap: 16px;
              margin-bottom: 20px;
              width: 100%;
            }
            .kpi-card {
              background: var(--erp-surface);
              border: 1px solid rgba(15, 23, 42, 0.04);
              border-radius: var(--erp-radius);
              padding: 20px 22px;
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 14px;
              box-shadow: 0 2px 6px rgba(15, 23, 42, 0.05), 0 8px 20px rgba(15, 23, 42, 0.05);
            }
            .kpi-icon {
              width: 46px;
              height: 46px;
              min-width: 46px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 18px;
              background: var(--erp-primary);
              color: #fff;
              order: 2;
            }
            .kpi-icon.success { background: var(--erp-success); color: #fff; }
            .kpi-icon.warning { background: var(--erp-warning); color: #fff; }
            .kpi-icon.danger { background: var(--erp-danger); color: #fff; }
            .kpi-body { display: flex; flex-direction: column; min-width: 0; order: 1; }
            .kpi-label {
              font-size: 13px;
              color: var(--erp-text-muted);
              font-weight: 500;
              font-style: italic;
            }
            .kpi-value {
              font-size: 22px;
              font-weight: 700;
              margin-top: 6px;
              color: var(--erp-text);
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .kpi-sub { font-size: 12.5px; color: var(--erp-text-muted); font-style: italic; margin-top: 6px; }
            .kpi-trend { font-size: 12.5px; color: var(--erp-success); font-style: italic; margin-top: 6px; }

            /* Sections */
            .erp-section { margin-bottom: 26px; }
            .erp-section-title {
              display: flex;
              align-items: center;
              gap: 8px;
              margin-bottom: 12px;
              font-size: 14px;
              font-weight: 700;
              color: var(--erp-text);
              text-transform: uppercase;
              letter-spacing: 0.04em;
            }
            .erp-section-title .bar {
              width: 4px;
              height: 16px;
              background: var(--erp-primary);
              border-radius: 2px;
            }
            .erp-section-desc {
              font-size: 12.5px;
              color: var(--erp-text-muted);
              margin: -8px 0 12px 12px;
            }

            .panel-grid { display: grid; gap: 16px; width: 100%; }
            .cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
            .cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
            .col-span-2 { grid-column: span 2; }

            .panel-card {
              background: var(--erp-surface);
              border: 1px solid rgba(15, 23, 42, 0.04);
              border-radius: var(--erp-radius);
              padding: 18px 20px;
              box-shadow: 0 2px 6px rgba(15, 23, 42, 0.05), 0 8px 20px rgba(15, 23, 42, 0.05);
              min-width: 0;
              overflow: hidden;
            }
            .panel-card h6, .panel-card h5 {
              font-size: 13px;
              font-weight: 600;
              font-style: italic;
              color: var(--erp-text-muted);
              margin: 0 0 12px;
            }
            .panel-card.clickable { cursor: pointer; }
            .panel-card.clickable:hover { box-shadow: 0 4px 10px rgba(15, 23, 42, 0.07), 0 10px 24px rgba(15, 23, 42, 0.07); }

            .mini-stat { background: var(--erp-surface); border: 1px solid rgba(15, 23, 42, 0.04); border-radius: var(--erp-radius); padding: 18px 20px; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.05), 0 8px 20px rgba(15, 23, 42, 0.05); }
            .mini-stat .mini-label { font-size: 13px; font-weight: 500; font-style: italic; color: var(--erp-text-muted); }
            .mini-stat .mini-value { font-size: 20px; font-weight: 700; margin-top: 8px; }

            .legend-row { display: flex; justify-content: space-between; align-items: center; font-size: 12.5px; padding: 7px 0; border-bottom: 1px solid rgba(15, 23, 42, 0.05); }
            .legend-row:last-child { border-bottom: none; }
            .legend-dot { width: 10px; height: 10px; border-radius: 3px; display: inline-block; margin-right: 8px; }

            .gauge-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; text-align: center; }
            .gauge-grid .gauge-wrap { width: 64px; height: 64px; margin: 0 auto 6px; }
            .gauge-grid small { color: var(--erp-text-muted); font-size: 11.5px; }

            .due-item { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid var(--erp-border); }
            .due-item:last-child { border-bottom: none; }
            .due-title { font-size: 13px; font-weight: 600; }
            .due-date { font-size: 11.5px; color: var(--erp-text-muted); }
            .empty-state { text-align: center; padding: 24px 0; color: var(--erp-text-muted); }

            @media (max-width: 1200px) {
              .kpi-row { grid-template-columns: repeat(2, 1fr); }
              .cols-3, .cols-4 { grid-template-columns: repeat(2, 1fr); }
            }
            @media (max-width: 700px) {
              .kpi-row, .cols-2, .cols-3, .cols-4 { grid-template-columns: 1fr; }
              .col-span-2 { grid-column: span 1; }
            }
          `}</style>

          {/* Header + filter */}
          <div className="erp-header">
            <div>
              <h4>Dashboard</h4>
              <p>Finance, sales and operations at a glance</p>
            </div>
            <div className="erp-filter-bar">
              <DropdownButton
                id="financial-year-dropdown"
                title='Filter'
                onSelect={handleFinancialYearChange}
                label='filter'
                variant="outline-primary"
              >
                <Dropdown.Item eventKey="financialYear">
                  This Financial Year
                </Dropdown.Item>
                <Dropdown.Item eventKey="custom">
                  Select Year
                </Dropdown.Item>
              </DropdownButton>

              {selectedFilter === "customRange" && isDateFilterVisible && (
                <div className="d-flex align-items-end gap-3">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      views={["year", "month"]}
                      openTo="year"
                      label="Start Month"
                      format="MMM YYYY"
                      value={customDateRange.startDate ? dayjs(customDateRange.startDate, "YYYY-MM") : null}
                      onChange={(newValue) => {
                        if (newValue) {
                          const currentStart = customDateRange.startDate ? dayjs(customDateRange.startDate, "YYYY-MM") : null;
                          if (!currentStart || currentStart.year() !== newValue.year()) {
                            // Year changed, auto set to Jan - Dec
                            handleCustomDateChange(`${newValue.year()}-01`, `${newValue.year()}-12`);
                          } else {
                            // Only month changed
                            handleCustomDateChange(newValue.format("YYYY-MM"), customDateRange.endDate);
                          }
                        } else {
                          handleCustomDateChange(null, customDateRange.endDate);
                        }
                      }}
                      slotProps={{
                        textField: { size: "small", className: "form-feild", sx: { width: 150 } },
                        popper: { disablePortal: true, sx: { zIndex: 2000 } },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      views={["year", "month"]}
                      openTo="year"
                      label="End Month"
                      format="MMM YYYY"
                      value={customDateRange.endDate ? dayjs(customDateRange.endDate, "YYYY-MM") : null}
                      onChange={(newValue) => {
                        if (newValue) {
                          const currentEnd = customDateRange.endDate ? dayjs(customDateRange.endDate, "YYYY-MM") : null;
                          if (!currentEnd || currentEnd.year() !== newValue.year()) {
                            // Year changed, auto set to Jan - Dec
                            handleCustomDateChange(`${newValue.year()}-01`, `${newValue.year()}-12`);
                          } else {
                            // Only month changed
                            handleCustomDateChange(customDateRange.startDate, newValue.format("YYYY-MM"));
                          }
                        } else {
                          handleCustomDateChange(customDateRange.startDate, null);
                        }
                      }}
                      slotProps={{
                        textField: { size: "small", className: "form-feild", sx: { width: 150 } },
                        popper: { disablePortal: true, sx: { zIndex: 2000 } },
                      }}
                    />
                  </LocalizationProvider>
                  <button onClick={handleClearDates} className="btn btn-secondary">Clear</button>
                  <button onClick={handleSubmit} className="btn btn-primary btn-sm">Submit</button>
                </div>
              )}
            </div>
          </div>

          {/* KPI strip */}
          <div className="kpi-row">
            <div className="kpi-card">
              <div className="kpi-icon"><FiDollarSign /></div>
              <div className="kpi-body">
                <span className="kpi-label">Total Revenue</span>
                <span className="kpi-value">{formatUSCurrency(totalRevenue)}</span>
              </div>
            </div>
            <div className="kpi-card">
              <div className={`kpi-icon ${netProfit >= 0 ? 'success' : 'danger'}`}>
                {netProfit >= 0 ? <FiTrendingUp /> : <FiTrendingDown />}
              </div>
              <div className="kpi-body">
                <span className="kpi-label">Net Profit</span>
                <span className="kpi-value">{formatUSCurrency(netProfit)}</span>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-icon warning"><FiFileText /></div>
              <div className="kpi-body">
                <span className="kpi-label">VAT Payable</span>
                <span className="kpi-value">{Number(vatPayable).toLocaleString()}</span>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-icon success"><FiCreditCard /></div>
              <div className="kpi-body">
                <span className="kpi-label">Cash &amp; Bank</span>
                <span className="kpi-value">{formatUSCurrency(totalBalance)}</span>
                <span className="kpi-sub">Cash {formatUSCurrency(cashBalance)} · Bank {formatUSCurrency(bankBalance)}</span>
              </div>
            </div>
          </div>

          {/* Finance overview */}
          <div className="erp-section">
            <div className="erp-section-title"><span className="bar" /> Finance Overview</div>
            <div className="panel-grid cols-3">
              <div className="panel-card" style={{ display: 'flex', flexDirection: 'column' }}>
                <h6>Income vs Expenses</h6>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '50%' }}>
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Income', value: Math.max(0, totalRevenue) },
                            { name: 'Expenses', value: Math.max(0, totalExpense) }
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={65}
                          paddingAngle={2}
                          dataKey="value"
                          label={false}
                          stroke="none"
                          onClick={(data, index) => {
                            if (data.name === 'Income') handleShowModal('income');
                            else handleShowModal('expense');
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <Cell key="income" fill="#10B981" />
                          <Cell key="expense" fill="#EF4444" />
                        </Pie>
                        <Tooltip formatter={(value) => [formatUSCurrency(value)]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{ width: '50%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div 
                      className="p-2 rounded" 
                      style={{ borderLeft: '3px solid #10B981', backgroundColor: '#f0fdf4', cursor: 'pointer' }}
                      onClick={() => handleShowModal('income')}
                    >
                      <div className="text-muted" style={{ fontSize: '12px' }}>Total Income</div>
                      <strong className="text-success">{formatUSCurrency(totalRevenue)}</strong>
                    </div>
                    <div 
                      className="p-2 rounded" 
                      style={{ borderLeft: '3px solid #EF4444', backgroundColor: '#fef2f2', cursor: 'pointer' }}
                      onClick={() => handleShowModal('expense')}
                    >
                      <div className="text-muted" style={{ fontSize: '12px' }}>Total Expense</div>
                      <strong className="text-danger">{formatUSCurrency(totalExpense)}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="panel-card">
                <h6>VAT Overview</h6>
                <div className="legend-row">
                  <span className="text-muted">VAT Collected</span>
                  <strong>{((summary?.vat?.out ?? corporateTaxData?.output_vat) ?? 0).toLocaleString()}</strong>
                </div>
                <div className="legend-row">
                  <span className="text-muted">Vendor VAT paid</span>
                  <strong>{((summary?.vat?.in ?? corporateTaxData?.input_vat) ?? 0).toLocaleString()}</strong>
                </div>
                <div className="legend-row">
                  <span className="text-muted">Net VAT payable</span>
                  <strong>{Number(vatPayable).toLocaleString()}</strong>
                </div>
              </div>

              <div className="panel-card">
                <h6>Sales by Region</h6>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie
                      data={summary.sales_by_region || []}
                      dataKey="value"
                      nameKey="region"
                      outerRadius={60}
                      label={false}
                      stroke="none"
                    >
                      {(summary.sales_by_region || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="panel-grid cols-2" style={{ marginTop: 16 }}>
              <div className="panel-card">
                <h6>Net Profit Trend</h6>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={summary.net_profit?.monthly_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="profit"
                      stroke="#4F46E5"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="panel-card">
                <h6>Cash Flow</h6>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={prepareCashFlowData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis
                      domain={['dataMin - 100', 'dataMax + 100']}
                      tickFormatter={(value) => formatCurrency(value)}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip
                      formatter={(value) => [formatCurrency(value), 'Net Cash Flow']}
                      labelFormatter={(day) => `Day: ${day}`}
                    />
                    <Area
                      type="monotone"
                      dataKey="net_cash"
                      name="Net Cash Flow"
                      stroke="#4F46E5"
                      fill="#4F46E5"
                      fillOpacity={0.15}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="panel-grid cols-2" style={{ marginTop: 16 }}>
              <div className="panel-card">
                <h6>Top 5 Products</h6>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    layout="vertical"
                    data={summary.top_products.map((item, index) => ({
                      name: item.product?.product_name || `Product #${item.product_id}`,
                      quantity: item.total_quantity,
                      fill: COLORS[index % COLORS.length],
                    }))}
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" hide />
                    <Tooltip />
                    <Bar dataKey="quantity">
                      {summary.top_products.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="panel-card">
                <h6>Monthly Expenses</h6>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={summary.monthly_expenses || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      tickFormatter={(value) => `${value.toLocaleString()}`}
                      domain={[0, 'dataMax + 50']}
                      tickCount={6}
                      ticks={[0, 100, 200, 300, 400, 500]}
                    />
                    <Tooltip
                      formatter={(value) => [`${value.toLocaleString()}`]}
                      labelFormatter={(month) => `Month: ${month}`}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="total"
                      name="Expenses"
                      stroke="#EF4444"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Sales */}
          <div className="erp-section">
            <div className="erp-section-title"><span className="bar" /> Sales</div>
            <div className="panel-grid cols-4">
              <div className="mini-stat">
                <div className="mini-label">Total Sales</div>
                <div className="mini-value">{summary?.total?.toLocaleString() || 0}</div>
              </div>
              <div className="mini-stat">
                <div className="mini-label">Conversion Rate</div>
                <div className="mini-value">{summary?.conversion_rate || 0}%</div>
              </div>
              <div className="mini-stat">
                <div className="mini-label">Avg Order Value</div>
                <div className="mini-value">{summary?.avg_order_value?.toLocaleString() || 0}</div>
              </div>
              <div className="mini-stat">
                <div className="mini-label">New Customers</div>
                <div className="mini-value">{summary?.new_customers || 0}</div>
              </div>
            </div>

            <div className="panel-grid cols-3" style={{ marginTop: 16 }}>
              <div className="panel-card">
                <h6>Sales Target</h6>
                <div style={{ width: 90, height: 90, margin: "0 auto" }}>
                  <CircularProgressbar
                    value={summary?.target_progress || 0}
                    text={`${summary?.target_progress || 0}%`}
                    strokeWidth={12}
                    styles={buildStyles({
                      textSize: "16px",
                      pathColor: "#10B981",
                      textColor: "#0F172A",
                      trailColor: "#E6E9F0",
                    })}
                  />
                </div>
              </div>

              <div className="panel-card">
                <h6>Sales by Product</h6>
                {summary?.by_product?.length ? (
                  <ResponsiveContainer width="100%" height={110}>
                    <BarChart
                      data={summary?.by_product
                        .map(item => ({ ...item, summary: parseFloat(item.summary) }))
                        .sort((a, b) => b.summary - a.summary)}
                      layout="vertical"
                      margin={{ top: 5, right: 10, bottom: 5, left: 5 }}
                      barCategoryGap={2}
                      barGap={2}
                    >
                      <defs>
                        <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#4F46E5" stopOpacity={1} />
                          <stop offset="100%" stopColor="#A5B4FC" stopOpacity={1} />
                        </linearGradient>
                      </defs>
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="product" hide />
                      <Tooltip formatter={(value) => `₹ ${value.toLocaleString()}`} />
                      <Bar dataKey="sales" radius={[4, 4, 4, 4]} fill="url(#barGradient)" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted">No product summary available</p>
                )}
              </div>

              <div className="panel-card">
                <h6>Pending Quotations &amp; Invoices</h6>
                <div className="kpi-value" style={{ fontSize: 26 }}>{summary?.pending_documents || 0}</div>
                <p className="kpi-sub">Awaiting action</p>
              </div>
            </div>
          </div>

          {/* Operations */}
          <div className="erp-section">
            <div className="erp-section-title"><span className="bar" /> Operations</div>
            <div className="panel-grid cols-3">
              <div className="mini-stat">
                <div className="mini-label">Orders in Progress</div>
                <div className="mini-value">{ordersInProgress}</div>
                <div className="progress mt-2" style={{ height: '6px' }}>
                  <div className="progress-bar bg-warning" style={{ width: `${progressPercent1}%` }}></div>
                </div>
                <small className="text-muted">{Math.round(progressPercent1)}% of total orders</small>
              </div>

              <div className="mini-stat">
                <div className="mini-label">Avg Fulfillment Time</div>
                <div className="mini-value">
                  {(summary?.avg_order_fulfillment_time / 60 / 24).toFixed(1)} <small>days</small>
                </div>
              </div>

              <div className="panel-card">
                <h6>Project Summary</h6>
                <div className="gauge-grid">
                  <div>
                    <div className="gauge-wrap">
                      <CircularProgressbar
                        value={completedPercent}
                        text={`${completedPercent}%`}
                        styles={buildStyles({ textSize: '18px', pathColor: '#10B981', trailColor: '#E6E9F0', textColor: '#0F172A' })}
                      />
                    </div>
                    <small>Completed</small>
                  </div>
                  <div>
                    <div className="gauge-wrap">
                      <CircularProgressbar
                        value={inProgressPercent}
                        text={`${inProgressPercent}%`}
                        styles={buildStyles({ textSize: '18px', pathColor: '#F59E0B', trailColor: '#E6E9F0', textColor: '#0F172A' })}
                      />
                    </div>
                    <small>In Progress</small>
                  </div>
                  <div>
                    <div className="gauge-wrap">
                      <CircularProgressbar
                        value={pendingPercent}
                        text={`${pendingPercent}%`}
                        styles={buildStyles({ textSize: '18px', pathColor: '#EF4444', trailColor: '#E6E9F0', textColor: '#0F172A' })}
                      />
                    </div>
                    <small>Pending</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Accounts */}
          <div className="erp-section">
            <div className="erp-section-title"><span className="bar" /> Accounts</div>

            <div className="panel-grid cols-3">
              <div className="panel-card">
                <h6>Profit &amp; Loss</h6>
                <div className="legend-row">
                  <span className="text-muted">Income</span>
                  <strong className="text-success">{formatUSCurrency(totalRevenue)}</strong>
                </div>
                <div className="legend-row">
                  <span className="text-muted">Expenses</span>
                  <strong className="text-danger">{formatUSCurrency(totalExpense)}</strong>
                </div>
                <div className="legend-row">
                  <span className="text-muted">Net Profit</span>
                  <strong className={netProfit >= 0 ? 'text-success' : 'text-danger'}>{formatUSCurrency(netProfit)}</strong>
                </div>
              </div>

              <div className="panel-card">
                <h6>Account Summary</h6>
                <div className="legend-row">
                  <span className="text-muted">Cash Balance</span>
                  <strong>{formatUSCurrency(cashBalance)}</strong>
                </div>
                <div className="legend-row">
                  <span className="text-muted">Bank Balance</span>
                  <strong>{formatUSCurrency(bankBalance)}</strong>
                </div>
                <div className="legend-row">
                  <span className="text-muted">Total Balance</span>
                  <strong className="text-primary">{formatUSCurrency(totalBalance)}</strong>
                </div>
                <div className="legend-row mt-2 pt-2 border-top">
                  <span className="text-muted">Net Profit</span>
                  <strong className={netProfit >= 0 ? "text-success" : "text-danger"}>
                    {formatUSCurrency(netProfit)}
                  </strong>
                </div>
              </div>

              <div className="panel-card">
                <h6>Expense Breakdown</h6>
                {summary.expense_breakdown?.length ? (
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie
                        data={summary.expense_breakdown.map(item => ({
                          name: item.category?.name || 'Uncategorized',
                          value: parseFloat(item.total)
                        }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={60}
                        label={false}
                        stroke="none"
                      >
                        {summary.expense_breakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted">No expense data available</p>
                )}
              </div>
            </div>

            <div className="panel-grid cols-2" style={{ marginTop: 16 }}>
              <div className="panel-card">
                <h6>Accounts Receivable</h6>
                <div className="row">
                  <div className="col-md-6" style={{ height: '220px' }}>
                    {prepareReceivablesData().some(item => item.value > 0) ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={prepareReceivablesData().filter(item => item.value > 0)}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={65}
                            innerRadius={38}
                            label={false}
                          >
                            {prepareReceivablesData()
                              .filter(item => item.value > 0)
                              .map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                          </Pie>
                          <Tooltip formatter={(value) => [`${value.toLocaleString()}`, 'Amount']} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="empty-state">
                        <FiPieChart size={28} style={{ opacity: 0.5, marginBottom: 8 }} />
                        <div>No outstanding receivables</div>
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    {prepareReceivablesData().map((item, index) => (
                      <div key={index} className="legend-row">
                        <span className="d-flex align-items-center">
                          <span
                            className="legend-dot"
                            style={{ backgroundColor: item.value > 0 ? COLORS[index % COLORS.length] : '#e5e7eb' }}
                          ></span>
                          {item.name}
                        </span>
                        <span className={`text-end ${item.value === 0 ? 'text-muted' : ''}`}>
                          <div>{formatUSCurrency(item.value)}</div>
                          <div className="small text-muted">{item.count} invoices</div>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="panel-card">
                <h6>Payment Reminders</h6>
                {summary.upcoming_dues?.length ? (
                  <div>
                    {summary.upcoming_dues.map((due, index) => {
                      const dueDate = new Date(due.due_date);
                      const today = new Date();
                      const daysRemaining = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

                      if (daysRemaining > 0 && daysRemaining <= 20) {
                        return (
                          <div key={index} className="due-item">
                            <div>
                              <div className="due-title">VAT Payment - {due.quarter}</div>
                              <div className="due-date">
                                Due: {dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </div>
                            </div>
                            <span className={`badge ${daysRemaining <= 7 ? 'bg-danger' : 'bg-warning'}`}>
                              {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} left
                            </span>
                          </div>
                        );
                      }
                      return null;
                    }).filter(Boolean)}
                  </div>
                ) : (
                  <div className="empty-state">
                    <FiCalendar size={28} style={{ opacity: 0.5, marginBottom: 8 }} />
                    <p className="font-size-12">No upcoming VAT payments within reminder period</p>
                  </div>
                )}
              </div>
            </div>

            <div className="panel-grid" style={{ marginTop: 16 }}>
              <div className="panel-card">
                <h6>Accounts Payable Trend</h6>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={preparePayablesData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis
                      domain={[0, 'dataMax + 50']}
                      tickCount={6}
                      ticks={[0, 1000, 2000, 3000, 4000, 5000]}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line type="monotone" dataKey="amount" name="Payable Amount" stroke="#E53E3E" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <Offcanvas show={showIncomeExpenseModal} onHide={() => setShowIncomeExpenseModal(false)} placement="end" style={{ width: '680px' }}>
            <Offcanvas.Header closeButton>
              <Offcanvas.Title>{modalType === 'income' ? 'Income Details' : 'Expense Details'}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              {modalType === 'income' ? (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Ref no</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary?.income_list?.length > 0 ? (
                      summary.income_list.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.date ? new Date(item.date).toLocaleDateString() : '-'}</td>
                          <td>{item.name}</td>
                          <td>{formatUSCurrency(item.total || item.amount || item.value || 0)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-muted">No detailed income breakdown available for the selected period.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Expense</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary?.expense_list?.length > 0 ? (
                      summary.expense_list.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.date ? new Date(item.date).toLocaleDateString() : '-'}</td>
                          <td>{item.name}</td>
                          <td>{formatUSCurrency(item.total || item.amount || item.value || 0)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-muted">No detailed expense breakdown available for the selected period.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </Offcanvas.Body>
          </Offcanvas>
        </div>
      </div>
    </div>
  );
}

export default FirstPage;
